import {announce} from '@github-ui/aria-live'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {
  CategorizedCopilotSpaces,
  CategorizedSpacesLoadingState,
  CopilotSpaceId,
  CopilotSpacesCounts,
  CopilotSpaceTab,
  IndexCopilotSpace,
} from '@github-ui/copilot-chat-types'
import {ListBlock} from '@github-ui/pacer/ListBlock'
import {matchesCommonSearch} from '@github-ui/pacer/search-utils'
import {
  BookIcon,
  CodeIcon,
  LockIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonIcon,
  ProjectIcon,
} from '@primer/octicons-react'
import {Button, Link, SkeletonBox, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './SpacesList.module.css'

interface CategoryState<T> {
  starred: T
  userOwned: T
  orgOwned: T
  sharedWithUser: T
}

export interface SpacesListProps {
  categorizedSpaces?: CategorizedCopilotSpaces
  categorizedLoading?: CategorizedSpacesLoadingState
  filteredCategorizedSpaces?: CategorizedCopilotSpaces
  filteredCategorizedLoading?: CategorizedSpacesLoadingState
  spacesCounts?: CopilotSpacesCounts
  isCountsLoading: boolean
  onDelete: (space: CopilotSpaceId) => Promise<void>
  onSpaceClick?: () => void
  searchQuery: string
  currentUserLogin: string
  categorizedFetchingMore?: CategoryState<boolean>
  categorizedHasMore?: CategoryState<boolean>
  categorizedFetchMore?: CategoryState<() => void>
  isIndividualAccessEnabled: boolean
  getSpaceHref: (space: IndexCopilotSpace) => string
  renderSpaceActions: (space: IndexCopilotSpace, onDelete: () => Promise<void>) => ReactNode
  renderCreateSpaceButton: () => ReactNode
  onLoadMore?: () => void
}

const categoryForTab = {
  starred: 'starred',
  personal: 'userOwned',
  org: 'orgOwned',
  shared: 'sharedWithUser',
} as const

function visibilityText(space: IndexCopilotSpace) {
  if (!space.shareable) return 'Private'
  if (space.collaborative) return 'Shared'
  if (
    space.baseRole === 'custom_copilot_reader' ||
    space.baseRole === 'custom_copilot_writer' ||
    space.baseRole === 'custom_copilot_admin' ||
    space.visibility === 'org_public'
  ) {
    return 'Shared'
  }
  return 'Private'
}

function DocsLink() {
  return (
    <Link
      href="https://docs.github.com/copilot/how-tos/provide-context/use-copilot-spaces/create-and-use-copilot-spaces"
      className="tmp-mt-4 f5"
      target="_blank"
      inline
      rel="noopener noreferrer"
    >
      Need help? Read the docs
    </Link>
  )
}

function FirstSpaceEmptyState({createButton}: {createButton: ReactNode}) {
  return (
    <li>
      <div className="tmp-my-8 d-flex flex-column gap-3 text-center">
        <h2 className="f3 fgColor-default">Start your first space</h2>
        <div className="d-flex flex-items-center flex-column">
          {createButton}
          <DocsLink />
        </div>
      </div>
      <div className={styles.benefitSections}>
        <Benefit icon={<CodeIcon size={16} />} title="Generate code">
          Produce code that follows your team&apos;s patterns.
        </Benefit>
        <Benefit icon={<BookIcon size={16} />} title="Share knowledge">
          Centralize docs so your team can quickly find answers.
        </Benefit>
        <Benefit icon={<ProjectIcon size={16} />} title="Plan projects">
          Create requirements and issues your team can ship.
        </Benefit>
      </div>
    </li>
  )
}

function Benefit({icon, title, children}: {icon: ReactNode; title: string; children: ReactNode}) {
  return (
    <div className={styles.benefitSection}>
      <div className={styles.benefitIcon}>{icon}</div>
      <h3 className={styles.benefitTitle}>{title}</h3>
      <p className={styles.benefitDescription}>{children}</p>
    </div>
  )
}

function Counter({loading, count}: {loading: boolean; count: number}) {
  return loading ? (
    <SkeletonBox width="1.75em" height="1.4em" className={styles.counter} />
  ) : (
    <span className={styles.counter}>{count}</span>
  )
}

function SpaceListItem({
  space,
  href,
  onClick,
  actions,
}: {
  space: IndexCopilotSpace
  href: string
  onClick?: () => void
  actions: ReactNode
}) {
  const OwnerIcon = space.ownerIsOrg ? OrganizationIcon : PersonIcon
  const visibility = visibilityText(space)
  const VisibilityIcon = visibility === 'Shared' ? PeopleIcon : LockIcon

  return (
    <ListBlock.Item href={href} onClick={onClick}>
      <ListBlock.Item.Title>{space.name}</ListBlock.Item.Title>
      <ListBlock.Item.Description>
        <div className={styles.description}>{space.description}</div>
      </ListBlock.Item.Description>
      <ListBlock.Item.Metadata>
        <div className={styles.metadata}>
          <span className={styles.metadataItem}>
            <OwnerIcon size={16} />
            {space.owner}
          </span>
          <span className={styles.metadataItem}>
            <VisibilityIcon size={16} />
            {visibility}
          </span>
        </div>
      </ListBlock.Item.Metadata>
      <ListBlock.Item.Actions>{actions}</ListBlock.Item.Actions>
    </ListBlock.Item>
  )
}

export function SpacesList({
  categorizedSpaces,
  categorizedLoading,
  filteredCategorizedSpaces,
  filteredCategorizedLoading,
  spacesCounts,
  isCountsLoading,
  onDelete,
  onSpaceClick,
  searchQuery,
  currentUserLogin,
  categorizedFetchingMore,
  categorizedHasMore,
  categorizedFetchMore,
  isIndividualAccessEnabled,
  getSpaceHref,
  renderSpaceActions,
  renderCreateSpaceButton,
  onLoadMore,
}: SpacesListProps) {
  const savedTab = copilotLocalStorage.getSpacesSelectedTab()
  const initialTab =
    !isIndividualAccessEnabled && (savedTab === 'personal' || savedTab === 'shared') ? 'starred' : savedTab
  const [activeTab, setActiveTab] = useState<CopilotSpaceTab>(initialTab)
  const category = categoryForTab[activeTab]
  const hasFilteredData = Object.values(filteredCategorizedSpaces ?? {}).some(value => value !== undefined)
  const spacesToDisplay = searchQuery && hasFilteredData ? filteredCategorizedSpaces : categorizedSpaces
  const loadingToDisplay = searchQuery && hasFilteredData ? filteredCategorizedLoading : categorizedLoading
  const activeSpaces = useMemo(() => spacesToDisplay?.[category] ?? [], [category, spacesToDisplay])
  const activeLoading = loadingToDisplay?.[category] ?? true
  const filteredSpaces = useMemo(
    () =>
      hasFilteredData || !searchQuery.trim()
        ? activeSpaces
        : activeSpaces.filter(space => matchesCommonSearch(space, searchQuery)),
    [activeSpaces, hasFilteredData, searchQuery],
  )

  const changeTab = useCallback((tab: CopilotSpaceTab) => {
    setActiveTab(tab)
    copilotLocalStorage.setSpacesSelectedTab(tab)
  }, [])
  const visibleTabs: CopilotSpaceTab[] = isIndividualAccessEnabled
    ? ['starred', 'personal', 'shared', 'org']
    : ['starred', 'org']
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tab: CopilotSpaceTab) => {
    const currentIndex = visibleTabs.indexOf(tab)
    const destinations: Record<string, number> = {
      ArrowRight: (currentIndex + 1) % visibleTabs.length,
      ArrowLeft: (currentIndex - 1 + visibleTabs.length) % visibleTabs.length,
      Home: 0,
      End: visibleTabs.length - 1,
    }
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- ARIA tabs require direct keyboard navigation.
    const nextIndex = destinations[event.key]
    if (nextIndex === undefined) return

    event.preventDefault()
    const nextTab = visibleTabs[nextIndex]
    if (!nextTab) return
    changeTab(nextTab)
    document.getElementById(`${nextTab}-tab`)?.focus()
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Match the existing behavior by selecting populated starred spaces.
    if ((categorizedSpaces?.starred?.length ?? 0) > 0) changeTab('starred')
  }, [categorizedSpaces?.starred?.length, changeTab])

  const counts = useMemo(() => {
    if (spacesCounts && !searchQuery.trim()) {
      return {
        starred: spacesCounts.starred_spaces,
        personal: spacesCounts.user_owned_spaces,
        org: spacesCounts.org_owned_spaces,
        shared: spacesCounts.shared_with_me_spaces,
      }
    }

    const count = (spaces: IndexCopilotSpace[] | undefined) => {
      if (!spaces) return 0
      if (!searchQuery.trim() || hasFilteredData) return spaces.length
      return spaces.filter(space => matchesCommonSearch(space, searchQuery)).length
    }

    return {
      starred: count(spacesToDisplay?.starred),
      personal: count(spacesToDisplay?.userOwned),
      org: count(spacesToDisplay?.orgOwned),
      shared: count(spacesToDisplay?.sharedWithUser),
    }
  }, [hasFilteredData, searchQuery, spacesCounts, spacesToDisplay])

  const allSpaces = useMemo(
    () => [
      ...(categorizedSpaces?.starred ?? []),
      ...(categorizedSpaces?.userOwned ?? []),
      ...(categorizedSpaces?.orgOwned ?? []),
      ...(categorizedSpaces?.sharedWithUser ?? []),
    ],
    [categorizedSpaces],
  )
  const hasAnySpaces = allSpaces.length > 0
  const hasNotCreatedSpace =
    hasAnySpaces && !allSpaces.some(space => space.creator === currentUserLogin) && !activeLoading
  const hasNoMatches = hasAnySpaces && filteredSpaces.length === 0 && !activeLoading
  const isFetchingMore = categorizedFetchingMore?.[category] ?? false
  const hasMore = !searchQuery.trim() && (categorizedHasMore?.[category] ?? false)

  useEffect(() => {
    if (activeLoading || !searchQuery.trim()) return
    const count = filteredSpaces.length
    announce(count === 0 ? 'No spaces found.' : `${count} ${count === 1 ? 'space' : 'spaces'} found.`)
  }, [activeLoading, filteredSpaces.length, searchQuery])

  const previousCountRef = useRef(0)
  const wasFetchingMoreRef = useRef(false)
  useEffect(() => {
    if (isFetchingMore) {
      previousCountRef.current = filteredSpaces.length
      wasFetchingMoreRef.current = true
    } else if (wasFetchingMoreRef.current) {
      wasFetchingMoreRef.current = false
      const firstNewItem = document.querySelectorAll<HTMLElement>('#spaces-list a[href]')[previousCountRef.current]
      firstNewItem?.focus()
      if (firstNewItem) announce(`Loaded ${filteredSpaces.length - previousCountRef.current} more spaces`)
    }
  }, [filteredSpaces.length, isFetchingMore])

  const loadMore = () => {
    onLoadMore?.()
    categorizedFetchMore?.[category]()
  }

  const createButton = renderCreateSpaceButton()
  const emptyMessage = {
    starred: 'Spaces you star will appear here.',
    personal: 'Spaces you create will appear here.',
    org: 'Spaces belonging to your organizations will appear here.',
    shared: 'Shared spaces will appear here.',
  }[activeTab]

  return (
    <ListBlock>
      <ListBlock.Header>
        <div role="tablist" aria-label="Filter spaces" className={styles.filterButtons}>
          <Tab
            label="Starred"
            tab="starred"
            activeTab={activeTab}
            count={counts.starred}
            loading={isCountsLoading && !searchQuery}
            onSelect={changeTab}
            onKeyDown={handleTabKeyDown}
          />
          {isIndividualAccessEnabled && (
            <Tab
              label="Yours"
              tab="personal"
              activeTab={activeTab}
              count={counts.personal}
              loading={isCountsLoading && !searchQuery}
              onSelect={changeTab}
              onKeyDown={handleTabKeyDown}
            />
          )}
          {isIndividualAccessEnabled && (
            <Tab
              label="Shared with me"
              tab="shared"
              activeTab={activeTab}
              count={counts.shared}
              loading={isCountsLoading && !searchQuery}
              onSelect={changeTab}
              onKeyDown={handleTabKeyDown}
              className={styles.sharedSpacesTab}
            />
          )}
          <Tab
            label="Organizations"
            tab="org"
            activeTab={activeTab}
            count={counts.org}
            loading={isCountsLoading && !searchQuery}
            onSelect={changeTab}
            onKeyDown={handleTabKeyDown}
          />
        </div>
      </ListBlock.Header>
      <ListBlock.List loading={activeLoading} id="spaces-list" role="tabpanel" aria-labelledby={`${activeTab}-tab`}>
        {!hasAnySpaces && !activeLoading ? (
          <FirstSpaceEmptyState createButton={createButton} />
        ) : hasNotCreatedSpace && activeTab === 'personal' ? (
          <FirstSpaceEmptyState createButton={createButton} />
        ) : hasNoMatches ? (
          <ListBlock.EmptyState>
            <p>{emptyMessage}</p>
            {createButton}
            <DocsLink />
          </ListBlock.EmptyState>
        ) : (
          <>
            {filteredSpaces.map(space => (
              <SpaceListItem
                key={space.owner ? `${space.owner}/${space.id}` : space.id}
                space={space}
                href={getSpaceHref(space)}
                onClick={onSpaceClick}
                actions={renderSpaceActions(space, () => onDelete(space))}
              />
            ))}
            {hasMore && (
              <li className={styles.loadMoreContainer}>
                <Button
                  variant="invisible"
                  onClick={loadMore}
                  disabled={isFetchingMore}
                  className={styles.loadMoreButton}
                  aria-label={isFetchingMore ? 'Loading more spaces' : 'Load more spaces'}
                >
                  {isFetchingMore ? (
                    <span className={styles.loadMoreContent}>
                      <Spinner size="small" />
                      Loading...
                    </span>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </li>
            )}
          </>
        )}
      </ListBlock.List>
    </ListBlock>
  )
}

function Tab({
  label,
  tab,
  activeTab,
  count,
  loading,
  onSelect,
  onKeyDown,
  className,
}: {
  label: string
  tab: CopilotSpaceTab
  activeTab: CopilotSpaceTab
  count: number
  loading: boolean
  onSelect: (tab: CopilotSpaceTab) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>, tab: CopilotSpaceTab) => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(tab)}
      role="tab"
      aria-selected={activeTab === tab}
      aria-controls="spaces-list"
      id={`${tab}-tab`}
      tabIndex={activeTab === tab ? 0 : -1}
      onKeyDown={event => onKeyDown(event, tab)}
      className={clsx(styles.button, className)}
    >
      {label} <Counter loading={loading} count={count} />
    </button>
  )
}
